import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { BreadcrumbListLD, WebPageLD } from '@/components/base/SEO';
import { categories, allVideos, getVideosByCategory, Video } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const videos = useMemo(() => {
    let filtered = activeSlug === 'all' ? allVideos : getVideosByCategory(activeSlug);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.title.toLowerCase().includes(query) ||
          v.description.toLowerCase().includes(query) ||
          v.category.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [activeSlug, searchQuery]);

  const allCatList = [{ name: '全部', slug: 'all', icon: 'ri-apps-line' }, ...categories];

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 桔子视频西瓜视频免费在线观看高清全集`
    : '全部视频 - 桔子视频西瓜视频免费在线观看高清全集';

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const breadcrumbItems = [
    { name: '首页', url: 'https://juzivideo.com/' },
    { name: currentCategory ? currentCategory.name : '全部视频', url: `https://juzivideo.com/category/${activeSlug}` },
  ];

  return (
    <>
      <ScrollToTop />
      <BreadcrumbListLD items={breadcrumbItems} />
      <WebPageLD name={pageTitle} description={`桔子视频${currentCategory ? currentCategory.name : '全部'}视频分类，提供西瓜视频、丝瓜视频高清全集不缓冲免费在线观看。`} url={`https://juzivideo.com/category/${activeSlug}`} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <nav className="w-full px-4 md:px-6 py-4" aria-label="面包屑导航">
            <div className="max-w-7xl mx-auto flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-400 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">
                {currentCategory ? currentCategory.name : '全部视频'}
              </span>
            </div>
          </nav>

          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {currentCategory ? currentCategory.name : '全部视频'}
                </h1>
                {searchQuery && (
                  <p className="mt-2 text-sm text-foreground-500">
                    搜索 "<strong className="text-primary-400">{searchQuery}</strong>" 的结果，共找到 {videos.length} 部视频
                  </p>
                )}
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2 flex-nowrap" role="tablist">
                {allCatList.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                    role="tab"
                    aria-selected={activeSlug === cat.slug}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200 border border-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Video Grid */}
              {videos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 pb-12" data-product-shop="">
                  {videos.map((video: Video) => (
                    <Link
                      key={video.id}
                      to={`/category/${video.categorySlug}`}
                      className="group block cursor-pointer"
                    >
                      <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200 mb-3">
                        <img
                          src={video.coverImage}
                          alt={video.title}
                          title={`${video.title} - 桔子视频高清在线观看`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                            <i className="ri-play-fill text-2xl text-background-50"></i>
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-semibold bg-background-50/90 text-primary-500">
                          {video.rating}
                        </div>
                        <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded text-xs bg-primary-500/90 text-background-50">
                          {video.episodes}
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-400 transition-colors line-clamp-1 mb-1">
                        {video.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-foreground-500">
                        <span>{video.year}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-500"></span>
                        <span>{video.views}次播放</span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-search-line text-3xl text-foreground-500"></i>
                  </div>
                  <p className="text-foreground-500 mb-4">没有找到相关视频</p>
                  <Link
                    to="/category/all"
                    className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    查看全部视频
                  </Link>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}